// Key-custody persistence for the dashboard "generate & enroll" flow. The
// generated keypair lives only in component state, which dies if the page
// unmounts (sidebar/sign-out/back navigation) or reloads while the enrollment
// POST is in flight — stranding a private key for a registration that may have
// been created server-side. To survive that, the material is stashed in
// sessionStorage BEFORE the POST and resurfaced via a recovery banner on the
// next mount, until the subject downloads (or discards) it.
//
// sessionStorage (not localStorage) is deliberate: it survives reload + in-tab
// navigation but is scoped to the tab and cleared when the tab closes, so a
// transient private key is not persisted indefinitely. It is cleared the moment
// the key is downloaded or discarded. The key never leaves the browser.

import type { EnrollmentMaterial } from "./registration-crypto";
import type { Registration } from "../api/client";
import { getSessionToken } from "../api/client";

const KEY = "sequesign.pending_registration";

export type PendingRegistration = {
  material: EnrollmentMaterial;
  role: "approver" | "counterparty";
  partyType?: "human" | "agent";
  identity: string;
  // Present once the POST confirmed; lets the recovery banner show the issued
  // ref too. Absent after an ambiguous failure (the ref is still recoverable
  // from the registrations list).
  registration?: Registration;
};

// The stored envelope binds the entry to the session that created it, so a
// pending key from one account never surfaces for another account that signs in
// on the same tab (sessionStorage is per-tab, not per-account).
type StoredEntry = PendingRegistration & { ownerSessionToken: string };

// `ownerSessionToken` is the token captured when the enrollment STARTED — passed
// in rather than read here, because by the time an in-flight write lands the
// caller may have signed out (getSessionToken() would be null), and a
// no-session entry would both match a signed-out read and be silently cleared on
// the next login, losing the key. We also refuse to persist without a token.
export function writePendingRegistration(
  pending: PendingRegistration,
  ownerSessionToken: string | null
): void {
  if (!ownerSessionToken) return;
  try {
    const entry: StoredEntry = { ...pending, ownerSessionToken };
    sessionStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage unavailable (private mode quota / disabled). The in-dialog
    // flow still works; only the cross-unmount recovery net is unavailable.
  }
}

export function readPendingRegistration(): PendingRegistration | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredEntry;
    // Only surface the entry to the account that created it. A different (or
    // signed-out) session must never see another account's private key.
    if (parsed && parsed.ownerSessionToken !== (getSessionToken() ?? "")) {
      clearPendingRegistration();
      return null;
    }
    // Minimal shape check: a usable entry must carry the private key.
    if (
      parsed &&
      parsed.material &&
      typeof parsed.material.privateKeyPem === "string" &&
      typeof parsed.material.publicKeyPem === "string" &&
      typeof parsed.identity === "string"
    ) {
      const { ownerSessionToken: _ownerSessionToken, ...pending } = parsed;
      return pending;
    }
  } catch {
    // Malformed entry; treat as absent and clear it below.
  }
  clearPendingRegistration();
  return null;
}

export function clearPendingRegistration(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
